import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import SupplierTable from "./SupplierListTable";
import NoSupplierListFound from "./NoSupplierListFound";
import AddSupplierModal from "./AddSupplierModal";
import AppButton from "../common/AppButton";
import EditSupplierModal from "./EditSupplierModal";
import ViewSupplierModal from "./ViewSupplierModal";
import { Supplier } from "./types/supplier";
import { useGetSupplierList } from "../../hooks/api/useSupplier";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";

export interface SupplierListProps {
  /** Store from route; list and create are scoped to this store only. */
  contextStoreId: string;
}

const SupplierList: React.FC<SupplierListProps> = ({ contextStoreId }) => {
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openViewModal, setOpenViewModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
    null
  );

  const { t } = useKivunovaTranslation();

  const supplierList = useSelector(
    (state: RootState) => state.suppliers.supplierList
  );

  const { mutate: fetchSuppliers, isPending } = useGetSupplierList();

  const storeId = String(contextStoreId ?? "").trim();

  useEffect(() => {
    if (!storeId) return;
    fetchSuppliers({ storeId });
  }, [storeId, fetchSuppliers]);

  const handleEdit = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setOpenEditModal(true);
  };

  const handleView = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setOpenViewModal(true);
  };

  if (isPending) {
    return (
      <CommonLoader
        loadingMessage={t("supplier-loading", "Loading suppliers...")}
      />
    );
  }

  if (!supplierList.length) {
    return (
      <>
        <NoSupplierListFound onAddClick={() => setOpenAddModal(true)} />
        <AddSupplierModal
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
          fixedStoreId={storeId || undefined}
        />
      </>
    );
  }

  return (
    <Box width="100%">
      <Box display="flex" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Box flexGrow={1} display="flex" justifyContent="flex-end">
          <AppButton variant="primary" onClick={() => setOpenAddModal(true)}>
            {t("supplier-add-new", "Add New Supplier")}
          </AppButton>
        </Box>
      </Box>

      <SupplierTable
        suppliers={supplierList}
        title={t("supplier-list-title", "Suppliers")}
        onEdit={handleEdit}
        onView={handleView}
      />

      <AddSupplierModal
        open={openAddModal}
        onClose={() => setOpenAddModal(false)}
        fixedStoreId={storeId || undefined}
      />

      {selectedSupplier && (
        <>
          <EditSupplierModal
            open={openEditModal}
            onClose={() => {
              setOpenEditModal(false);
              setSelectedSupplier(null);
            }}
            supplier={selectedSupplier}
          />

          <ViewSupplierModal
            open={openViewModal}
            onClose={() => {
              setOpenViewModal(false);
              setSelectedSupplier(null);
            }}
            supplier={selectedSupplier}
            onDelete={() => {
              setOpenViewModal(false);
              setSelectedSupplier(null);
            }}
          />
        </>
      )}
    </Box>
  );
};

export default SupplierList;
